import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { CANCEL_ON_NAVIGATION } from 'reg-hub-common';
import { OnGroupDataMessageArgs, WebPubSubClient } from '@azure/web-pubsub-client';
import { Observable, ReplaySubject, BehaviorSubject, firstValueFrom } from 'rxjs';
import { Constants } from 'projects/reg-hub-admin/src/constants';
import { EnvironmentUrlService } from '../../environment-url/environment-url.service';

const START_RETRY_DELAY_MS = 10000;

@Injectable({
  providedIn: 'root'
})
export class OrdersAuditsHubService implements OnDestroy {
  private client: WebPubSubClient | undefined;
  private messagesSubject = new ReplaySubject<any>(1);  // stores last message, can be Subject if you prefer
  public messages$: Observable<any> = this.messagesSubject.asObservable();

  private pendingAuditCount = 'PendingAuditCount';

  private joinedGroups = new Set<string>();

  // BehaviorSubjects to store orders
  private pendingAuditCountSource = new BehaviorSubject<number>(0);
  pendingAuditCount$ = this.pendingAuditCountSource.asObservable();

  constructor(private http: HttpClient,
    private environmentUrl: EnvironmentUrlService
  ) {}

  connect(): void {
    // The client is created synchronously so a second connect(), even in the same tick, is a no-op
    // rather than a second negotiate and an orphaned connection
    if (this.client) {
      return;
    }

    this.client = new WebPubSubClient({
      // Negotiate on every (re)connect since access URLs expire
      getClientAccessUrl: async () => {
        const response = await firstValueFrom(
          this.http.get<{ url: string }>(`${this.environmentUrl.urlAddress}${Constants.ordersHubNegotiateUrl}`, { context: new HttpContext().set(CANCEL_ON_NAVIGATION, false) }));
        return response.url;
      }
    }, {
      // Each reconnect attempt negotiates with the API, so back off instead of retrying every second
      reconnectRetryOptions: { mode: "Exponential" }
    });

    // Set up event handlers
    this.client.on("connected", (e) => {
      console.log("Connected to Orders hub:", e.connectionId);
    });

    // handle messages to a group when received
    this.client.on("group-message", (messageEvent) => {
      this.handleGroupMessageReceived(messageEvent);
    });

    // handle disconnection from the hub
    this.client.on("disconnected", () => {
      console.warn("Disconnected from Web PubSub");
    });

    this.start();
  }

  // Once started, the client reconnects by itself, but a failed start (e.g. the negotiate fails) is never retried
  private start() {
    this.client?.start()
      .then(() => this.joinPendingAuditsGroup())
      .catch(err => {
        console.error('Error starting client', err);
        setTimeout(() => this.start(), START_RETRY_DELAY_MS);
      });
  }

  private joinGroupIfNotJoined(groupName: string) {
    if (!this.joinedGroups.has(groupName)) {
      this.client?.joinGroup(groupName)
        .then(() => {
          this.joinedGroups.add(groupName);
        })
    }
  }

  private leaveGroupIfJoined(groupName: string) {
    if (this.joinedGroups.has(groupName)) {
      this.client?.leaveGroup(groupName)
        .then(() => {
          this.joinedGroups.delete(groupName);
        })
    }
  }

  joinPendingAuditsGroup() {
    this.joinGroupIfNotJoined(this.pendingAuditCount);
  }

  leavePendingAuditsGroup() {
    this.leaveGroupIfJoined(this.pendingAuditCount);
  }

  handleGroupMessageReceived(messageEvent: OnGroupDataMessageArgs) {
    if (messageEvent.message.dataType === "text") {
      const group: string = messageEvent.message.group;
      const count = messageEvent.message.data as number;

      switch (group) {
        case this.pendingAuditCount:
          this.updatePendingAuditCount(count);
          break;
        default:
          // This shouldnt happen, we only subscribed to the three groups here? Maybe for Orders?
          console.warn(`Received a message from an unhandled group: ${group}`, messageEvent.message.data);
          break;
      }

      this.messagesSubject.next(count);
    }
  }

  setInitialAuditPendingCount(count: number) {
    this.pendingAuditCountSource.next(count);
  }

  private updatePendingAuditCount(count: number) {
    this.pendingAuditCountSource.next(count);
  }

  ngOnDestroy() {
    // Stop the connection if the service is destroyed (e.g., in certain testing scenarios)
    this.client?.stop();
    this.client = undefined;
  }

  toCamelCase(key: any, value: any) {
    if (value && typeof value === 'object'){
      for (var k in value) {
        // Special case: If the key is exactly 'ID', rename it to 'id'
        if (k === 'ID') {
          value['id'] = value[k];
          delete value[k];
        }
        // Otherwise, just lowercase the first letter
        else if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
          value[k.charAt(0).toLowerCase() + k.substring(1)] = value[k];
          delete value[k];
        }
      }
    }
    return value;
  }
}